import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ChevronUp, ChevronDown, Plus, MessageSquare, FileText, Layout, Link2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { nodesAPI } from '../../lib/api';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';

// Layout types
const LAYOUTS = {
  TREE_DOWN: 'tree-down',
  TREE_RIGHT: 'tree-right',
  RADIAL: 'radial',
};

// Custom Node Component for vertical layouts
const MindmapNodeVertical = ({ data, selected }) => {
  const isRoot = data.isRoot;
  const hasContent = data.content && data.content.trim().length > 0;
  const isHighlighted = data.isHighlighted || selected;
  
  const copyNodeLink = (e) => {
    e.stopPropagation();
    const url = new URL(window.location.href);
    url.searchParams.set('node', data.nodeId);
    navigator.clipboard.writeText(url.toString())
      .then(() => toast.success('Node link copied!'))
      .catch(() => toast.error('Failed to copy link'));
  };

  return (
    <div 
      className={`mindmap-node ${isRoot ? 'root' : ''} ${isHighlighted ? 'ring-2 ring-orange-500 ring-offset-2' : ''}`}
      style={{ minWidth: '160px', maxWidth: '200px' }}
      data-testid={`mindmap-node-${data.nodeId}`}
    >
      {!isRoot && <Handle type="target" position={Position.Top} className="!bg-slate-400" />}
      
      <div className="flex flex-col gap-2">
        <div className="font-medium text-sm leading-tight line-clamp-2">
          {data.label}
        </div>
        
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1">
            <button
              className={`vote-btn ${data.userVote === 'up' ? 'text-orange-500' : 'text-slate-400 hover:text-orange-500'}`}
              onClick={(e) => { e.stopPropagation(); data.onVote?.(data.nodeId, 'up'); }}
              data-testid={`upvote-btn-${data.nodeId}`}
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <span className={`font-medium ${data.voteScore > 0 ? 'text-orange-500' : data.voteScore < 0 ? 'text-red-500' : 'text-slate-500'}`}>
              {data.voteScore || 0}
            </span>
            <button
              className={`vote-btn ${data.userVote === 'down' ? 'text-red-500' : 'text-slate-400 hover:text-red-500'}`}
              onClick={(e) => { e.stopPropagation(); data.onVote?.(data.nodeId, 'down'); }}
              data-testid={`downvote-btn-${data.nodeId}`}
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center gap-1">
            <button
              className="text-slate-400 hover:text-blue-500 transition-colors p-0.5"
              onClick={copyNodeLink}
              title="Copy link to this node"
            >
              <Link2 className="w-3.5 h-3.5" />
            </button>
            {hasContent && (
              <button
                className="text-slate-400 hover:text-orange-500 transition-colors p-0.5"
                onClick={(e) => { e.stopPropagation(); data.onViewContent?.(data.nodeId); }}
                title="View details"
              >
                <FileText className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              className="text-slate-400 hover:text-orange-500 transition-colors p-0.5"
              onClick={(e) => { e.stopPropagation(); data.onComment?.(data.nodeId); }}
              title="Comments"
            >
              <MessageSquare className="w-3.5 h-3.5" />
            </button>
            <button
              className="text-slate-400 hover:text-green-500 transition-colors p-0.5"
              onClick={(e) => { e.stopPropagation(); data.onAddChild?.(data.nodeId); }}
              title="Add child"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        
        <div className="text-[10px] text-slate-400 truncate">
          by {data.authorName}
        </div>
      </div>
      
      <Handle type="source" position={Position.Bottom} className="!bg-slate-400" />
    </div>
  );
};

// Custom Node Component for horizontal layouts
const MindmapNodeHorizontal = ({ data, selected }) => {
  const isRoot = data.isRoot;
  const hasContent = data.content && data.content.trim().length > 0;
  const isHighlighted = data.isHighlighted || selected;
  
  const copyNodeLink = (e) => {
    e.stopPropagation();
    const url = new URL(window.location.href);
    url.searchParams.set('node', data.nodeId);
    navigator.clipboard.writeText(url.toString())
      .then(() => toast.success('Node link copied!'))
      .catch(() => toast.error('Failed to copy link'));
  };

  return (
    <div 
      className={`mindmap-node ${isRoot ? 'root' : ''} ${isHighlighted ? 'ring-2 ring-orange-500 ring-offset-2' : ''}`}
      style={{ minWidth: '140px', maxWidth: '180px' }}
      data-testid={`mindmap-node-${data.nodeId}`}
    >
      {!isRoot && <Handle type="target" position={Position.Left} className="!bg-slate-400" />}
      
      <div className="flex flex-col gap-2">
        <div className="font-medium text-sm leading-tight line-clamp-2">
          {data.label}
        </div>
        
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1">
            <button
              className={`vote-btn ${data.userVote === 'up' ? 'text-orange-500' : 'text-slate-400 hover:text-orange-500'}`}
              onClick={(e) => { e.stopPropagation(); data.onVote?.(data.nodeId, 'up'); }}
            >
              <ChevronUp className="w-3 h-3" />
            </button>
            <span className={`font-medium text-[10px] ${data.voteScore > 0 ? 'text-orange-500' : 'text-slate-500'}`}>
              {data.voteScore || 0}
            </span>
            <button
              className={`vote-btn ${data.userVote === 'down' ? 'text-red-500' : 'text-slate-400 hover:text-red-500'}`}
              onClick={(e) => { e.stopPropagation(); data.onVote?.(data.nodeId, 'down'); }}
            >
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>
          
          <div className="flex items-center gap-1">
            <button
              className="text-slate-400 hover:text-blue-500 transition-colors"
              onClick={copyNodeLink}
              title="Copy link"
            >
              <Link2 className="w-3 h-3" />
            </button>
            <button
              className="text-slate-400 hover:text-orange-500 transition-colors"
              onClick={(e) => { e.stopPropagation(); data.onComment?.(data.nodeId); }}
            >
              <MessageSquare className="w-3 h-3" />
            </button>
            <button
              className="text-slate-400 hover:text-green-500 transition-colors"
              onClick={(e) => { e.stopPropagation(); data.onAddChild?.(data.nodeId); }}
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
        </div>
        
        <div className="text-[9px] text-slate-400 truncate">
          {data.authorName}
        </div>
      </div>
      
      <Handle type="source" position={Position.Right} className="!bg-slate-400" />
    </div>
  );
};

// Tree Down Layout (top-to-bottom)
const buildTreeDownLayout = (nodes, rootId) => {
  const nodeMap = new Map(nodes.map(n => [n.node_id, n]));
  const positioned = [];
  const edges = [];
  
  const NODE_WIDTH = 180;
  const HORIZONTAL_GAP = 40;
  const VERTICAL_GAP = 100;
  
  // Calculate subtree widths first
  const getSubtreeWidth = (nodeId) => {
    const node = nodeMap.get(nodeId);
    if (!node) return 0;
    
    const children = node.children_ids || [];
    if (children.length === 0) return NODE_WIDTH;
    
    let totalWidth = 0;
    children.forEach((childId, i) => {
      totalWidth += getSubtreeWidth(childId);
      if (i < children.length - 1) totalWidth += HORIZONTAL_GAP;
    });
    
    return Math.max(NODE_WIDTH, totalWidth);
  };
  
  // Position nodes recursively
  const positionNode = (nodeId, x, y) => {
    const node = nodeMap.get(nodeId);
    if (!node) return;
    
    positioned.push({
      id: nodeId,
      type: 'mindmapNodeVertical',
      position: { x, y },
      data: {
        label: node.title,
        nodeId: node.node_id,
        isRoot: !node.parent_id,
        content: node.content,
        authorName: node.author_name,
        voteScore: node.vote_score,
      },
    });
    
    const children = node.children_ids || [];
    if (children.length === 0) return;
    
    // Calculate total width needed for children
    let totalChildWidth = 0;
    const childWidths = children.map(childId => {
      const width = getSubtreeWidth(childId);
      totalChildWidth += width;
      return width;
    });
    totalChildWidth += (children.length - 1) * HORIZONTAL_GAP;
    
    // Position children centered under parent
    let currentX = x + NODE_WIDTH / 2 - totalChildWidth / 2;
    
    children.forEach((childId, i) => {
      const childWidth = childWidths[i];
      const childX = currentX + childWidth / 2 - NODE_WIDTH / 2;
      const childY = y + VERTICAL_GAP;
      
      positionNode(childId, childX, childY);
      
      edges.push({
        id: `e-${nodeId}-${childId}`,
        source: nodeId,
        target: childId,
        type: 'smoothstep',
        style: { stroke: '#94a3b8', strokeWidth: 2 },
      });
      
      currentX += childWidth + HORIZONTAL_GAP;
    });
  };
  
  if (rootId) {
    const totalWidth = getSubtreeWidth(rootId);
    positionNode(rootId, totalWidth / 2 - NODE_WIDTH / 2, 0);
  }
  
  return { nodes: positioned, edges };
};

// Tree Right Layout (left-to-right expanding)
const buildTreeRightLayout = (nodes, rootId) => {
  const nodeMap = new Map(nodes.map(n => [n.node_id, n]));
  const positioned = [];
  const edges = [];
  
  const NODE_HEIGHT = 80;
  const HORIZONTAL_GAP = 220;
  const VERTICAL_GAP = 20;
  
  // Calculate subtree heights
  const getSubtreeHeight = (nodeId) => {
    const node = nodeMap.get(nodeId);
    if (!node) return 0;
    
    const children = node.children_ids || [];
    if (children.length === 0) return NODE_HEIGHT;
    
    let totalHeight = 0;
    children.forEach((childId, i) => {
      totalHeight += getSubtreeHeight(childId);
      if (i < children.length - 1) totalHeight += VERTICAL_GAP;
    });
    
    return Math.max(NODE_HEIGHT, totalHeight);
  };
  
  // Position nodes recursively
  const positionNode = (nodeId, x, y) => {
    const node = nodeMap.get(nodeId);
    if (!node) return;
    
    const subtreeHeight = getSubtreeHeight(nodeId);
    const nodeY = y + subtreeHeight / 2 - NODE_HEIGHT / 2;
    
    positioned.push({
      id: nodeId,
      type: 'mindmapNodeHorizontal',
      position: { x, y: nodeY },
      data: {
        label: node.title,
        nodeId: node.node_id,
        isRoot: !node.parent_id,
        content: node.content,
        authorName: node.author_name,
        voteScore: node.vote_score,
      },
    });
    
    const children = node.children_ids || [];
    if (children.length === 0) return;
    
    let currentY = y;
    
    children.forEach((childId) => {
      const childHeight = getSubtreeHeight(childId);
      
      positionNode(childId, x + HORIZONTAL_GAP, currentY);
      
      edges.push({
        id: `e-${nodeId}-${childId}`,
        source: nodeId,
        target: childId,
        type: 'smoothstep',
        style: { stroke: '#94a3b8', strokeWidth: 2 },
      });
      
      currentY += childHeight + VERTICAL_GAP;
    });
  };
  
  if (rootId) {
    const totalHeight = getSubtreeHeight(rootId);
    positionNode(rootId, 0, 0);
  }
  
  return { nodes: positioned, edges };
};

// Radial Layout
const buildRadialLayout = (nodes, rootId) => {
  const nodeMap = new Map(nodes.map(n => [n.node_id, n]));
  const positioned = [];
  const edges = [];
  
  const CENTER_X = 400;
  const CENTER_Y = 300;
  const RADIUS_INCREMENT = 180;
  
  // Get all nodes at each level
  const getLevels = (nodeId, level = 0, levels = {}) => {
    if (!levels[level]) levels[level] = [];
    levels[level].push(nodeId);
    
    const node = nodeMap.get(nodeId);
    if (node?.children_ids) {
      node.children_ids.forEach(childId => getLevels(childId, level + 1, levels));
    }
    
    return levels;
  };
  
  if (rootId) {
    const levels = getLevels(rootId);
    
    // Position root at center
    positioned.push({
      id: rootId,
      type: 'mindmapNodeVertical',
      position: { x: CENTER_X - 90, y: CENTER_Y - 40 },
      data: {
        label: nodeMap.get(rootId)?.title,
        nodeId: rootId,
        isRoot: true,
        content: nodeMap.get(rootId)?.content,
        authorName: nodeMap.get(rootId)?.author_name,
        voteScore: nodeMap.get(rootId)?.vote_score,
      },
    });
    
    // Position other levels in circles
    Object.keys(levels).forEach(levelStr => {
      const level = parseInt(levelStr);
      if (level === 0) return;
      
      const nodesAtLevel = levels[level];
      const radius = RADIUS_INCREMENT * level;
      const angleStep = (2 * Math.PI) / nodesAtLevel.length;
      const startAngle = -Math.PI / 2; // Start from top
      
      nodesAtLevel.forEach((nodeId, i) => {
        const node = nodeMap.get(nodeId);
        if (!node) return;
        
        const angle = startAngle + i * angleStep;
        const x = CENTER_X + radius * Math.cos(angle) - 90;
        const y = CENTER_Y + radius * Math.sin(angle) - 40;
        
        positioned.push({
          id: nodeId,
          type: 'mindmapNodeVertical',
          position: { x, y },
          data: {
            label: node.title,
            nodeId: node.node_id,
            isRoot: false,
            content: node.content,
            authorName: node.author_name,
            voteScore: node.vote_score,
          },
        });
        
        // Add edge to parent
        if (node.parent_id) {
          edges.push({
            id: `e-${node.parent_id}-${nodeId}`,
            source: node.parent_id,
            target: nodeId,
            type: 'straight',
            style: { stroke: '#94a3b8', strokeWidth: 2 },
          });
        }
      });
    });
  }
  
  return { nodes: positioned, edges };
};

// Node types configuration
const nodeTypes = {
  mindmapNodeVertical: MindmapNodeVertical,
  mindmapNodeHorizontal: MindmapNodeHorizontal,
};

export const MindmapViewer = ({ 
  nodes: rawNodes, 
  rootNodeId,
  questionId,
  highlightNodeId,
  readOnly = false,
  onAddChild,
  onVote,
  onComment,
  onViewContent,
  userVotes = {},
}) => {
  const { isAuthenticated } = useAuth();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [layout, setLayout] = useState(LAYOUTS.TREE_DOWN);
  const saveTimeoutRef = useRef(null);
  const positionsRef = useRef({});

  // Save positions to backend (debounced)
  const savePositions = useCallback(async (positions) => {
    if (!questionId || readOnly || Object.keys(positions).length === 0) return;
    
    try {
      await nodesAPI.savePositions(questionId, positions);
    } catch (error) {
      console.error('Failed to save positions:', error);
    }
  }, [questionId, readOnly]);

  // Handle node drag end - save positions
  const onNodeDragStop = useCallback((event, node) => {
    // Update positions ref
    positionsRef.current[node.id] = { x: node.position.x, y: node.position.y };
    
    // Debounce save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      savePositions(positionsRef.current);
      positionsRef.current = {}; // Clear after save
    }, 1000); // Save after 1 second of no dragging
  }, [savePositions]);

  // Memoize callback functions to prevent unnecessary re-renders
  const safeOnAddChild = useMemo(() => 
    (!readOnly && isAuthenticated) ? onAddChild : () => {},
    [readOnly, isAuthenticated, onAddChild]
  );
  const safeOnVote = useMemo(() => 
    (!readOnly && isAuthenticated) ? onVote : () => {},
    [readOnly, isAuthenticated, onVote]
  );
  const safeOnComment = useMemo(() => 
    !readOnly ? onComment : () => {},
    [readOnly, onComment]
  );
  const safeOnViewContent = useMemo(() => 
    !readOnly ? onViewContent : () => {},
    [readOnly, onViewContent]
  );

  // Build and position nodes based on layout
  useEffect(() => {
    if (rawNodes && rawNodes.length > 0) {
      let layoutResult;
      
      // Check if any nodes have saved positions
      const hasSavedPositions = rawNodes.some(n => n.position_x != null && n.position_y != null);
      
      if (hasSavedPositions) {
        // Use saved positions for nodes that have them
        const nodesMap = {};
        const edges = [];
        
        rawNodes.forEach(rawNode => {
          const isRoot = rawNode.parent_id === null;
          const nodeType = layout === LAYOUTS.TREE_RIGHT ? 'mindmapNodeHorizontal' : 'mindmapNodeVertical';
          
          // Use saved position or calculate default
          let position;
          if (rawNode.position_x != null && rawNode.position_y != null) {
            position = { x: rawNode.position_x, y: rawNode.position_y };
          } else {
            // Default position for nodes without saved positions
            position = { x: 0, y: 0 };
          }
          
          nodesMap[rawNode.node_id] = {
            id: rawNode.node_id,
            type: nodeType,
            position,
            data: {
              label: rawNode.title,
              content: rawNode.content,
              nodeId: rawNode.node_id,
              authorName: rawNode.author_name,
              voteScore: rawNode.vote_score || 0,
              isRoot,
            },
          };
          
          if (rawNode.parent_id) {
            edges.push({
              id: `e-${rawNode.parent_id}-${rawNode.node_id}`,
              source: rawNode.parent_id,
              target: rawNode.node_id,
              type: 'smoothstep',
              animated: false,
              style: { stroke: '#cbd5e1', strokeWidth: 2 },
            });
          }
        });
        
        layoutResult = { nodes: Object.values(nodesMap), edges };
      } else {
        // Use auto-layout
        switch (layout) {
          case LAYOUTS.TREE_RIGHT:
            layoutResult = buildTreeRightLayout(rawNodes, rootNodeId);
            break;
          case LAYOUTS.RADIAL:
            layoutResult = buildRadialLayout(rawNodes, rootNodeId);
            break;
          case LAYOUTS.TREE_DOWN:
          default:
            layoutResult = buildTreeDownLayout(rawNodes, rootNodeId);
        }
      }
      
      // Add callbacks, user votes, and highlight state to node data
      const nodesWithCallbacks = layoutResult.nodes.map(node => ({
        ...node,
        selected: node.id === highlightNodeId,
        data: {
          ...node.data,
          onAddChild: safeOnAddChild,
          onVote: safeOnVote,
          onComment: safeOnComment,
          onViewContent: safeOnViewContent,
          userVote: userVotes[node.id],
          isHighlighted: node.id === highlightNodeId,
        },
      }));
      
      setNodes(nodesWithCallbacks);
      setEdges(layoutResult.edges);
    }
  }, [rawNodes, rootNodeId, userVotes, safeOnAddChild, safeOnVote, safeOnComment, safeOnViewContent, setNodes, setEdges, layout, highlightNodeId]);

  const onNodeClick = useCallback((event, node) => {
    // Handle node click if needed
  }, []);

  return (
    <div className="w-full h-full relative" style={{ width: '100%', height: '100%', minHeight: '500px' }} data-testid="mindmap-viewer">
      {/* Layout Switcher */}
      <div className="absolute top-4 right-4 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="bg-white shadow-md" data-testid="layout-switcher">
              <Layout className="w-4 h-4 mr-2" />
              Layout
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem 
              onClick={() => setLayout(LAYOUTS.TREE_DOWN)}
              className={layout === LAYOUTS.TREE_DOWN ? 'bg-slate-100' : ''}
              data-testid="layout-tree-down"
            >
              Tree (Top-Down)
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setLayout(LAYOUTS.TREE_RIGHT)}
              className={layout === LAYOUTS.TREE_RIGHT ? 'bg-slate-100' : ''}
              data-testid="layout-tree-right"
            >
              Tree (Left-Right)
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setLayout(LAYOUTS.RADIAL)}
              className={layout === LAYOUTS.RADIAL ? 'bg-slate-100' : ''}
              data-testid="layout-radial"
            >
              Radial
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onNodeDragStop={onNodeDragStop}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3, minZoom: 0.3, maxZoom: 1.5 }}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={!readOnly}
      >
        <Background color="#e2e8f0" gap={20} />
        <Controls className="!bg-white !border-slate-200 !shadow-lg" />
        <MiniMap 
          className="!bg-white !border-slate-200"
          nodeColor={(node) => node.data?.isRoot ? '#0f172a' : '#f97316'}
          maskColor="rgba(248, 249, 250, 0.8)"
        />
      </ReactFlow>
    </div>
  );
};
